import { createTheme, CssBaseline, ThemeProvider } from '@mui/material';
import { CookiesProvider } from 'react-cookie';
import { ConfirmProvider } from 'material-ui-confirm';
import { SnackbarProvider } from 'notistack';
import AppRoutes from './Routes';
import { AppProvider } from './context/AppContext';
import { AuthProvider } from './context/AuthContext';
import PwaUpdatePrompt from './components/PwaUpdatePrompt';
import '@fontsource/inter';
import '@fontsource-variable/quicksand';
import { ptBR } from 'date-fns/locale/pt-BR';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorBoundaryComponent } from './components/Content';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';

function App() {
    const theme = createTheme(
        {
            shape: {
                borderRadius: 8,
            },
            palette: {
                background: {
                    default: '#f5f6f8',
                    paper: '#ffffff',
                },
                divider: '#e5e7eb',
            },
            components: {
                MuiCssBaseline: {
                    styleOverrides: {
                        body: {
                            backgroundColor: '#f5f6f8',
                        },
                    },
                },
                MuiAppBar: {
                    defaultProps: {
                        elevation: 0,
                    },
                },
                MuiDrawer: {
                    styleOverrides: {
                        modal: ({ theme }) => ({
                            zIndex: theme.zIndex.modal,
                        }),
                    },
                },
                MuiButton: {
                    defaultProps: {
                        size: 'small',
                    },
                    styleOverrides: {
                        root: {
                            borderRadius: 6,
                            fontWeight: 700,
                        },
                    },
                },
                MuiTextField: {
                    defaultProps: {
                        size: 'small',
                    },
                    styleOverrides: {
                        root: {
                            borderRadius: 2,
                        },
                    },
                },
                MuiOutlinedInput: {
                    styleOverrides: {
                        root: {
                            '& input[readonly]': {
                                cursor: 'not-allowed',
                                color: '#666',
                                backgroundColor: '#f7f7f5',
                            },
                        },
                    },
                },

                MuiMenuItem: {
                    defaultProps: {
                        dense: true,
                    },
                },
                MuiTextField: {
                    defaultProps: {
                        size: 'small',
                    },
                },
                MuiSelect: {
                    defaultProps: {
                        size: 'small',
                    },
                },
                MuiIconButton: {
                    defaultProps: {
                        size: 'small',
                    },
                },
                MuiCard: {
                    defaultProps: {
                        variant: 'outlined',
                    },
                    styleOverrides: {
                        root: {
                            backgroundColor: 'white',
                            borderColor: '#e5e7eb',
                        },
                    },
                },
                MuiCardHeader: {
                    styleOverrides: {
                        title: {
                            fontSize: '1rem',
                            fontWeight: 800,
                        },
                    },
                },
                MuiPaper: {
                    defaultProps: {
                        variant: 'outlined',
                    },
                    styleOverrides: {
                        root: {
                            borderColor: '#e5e7eb',
                        },
                    },
                },
                MuiDialogTitle: {
                    styleOverrides: {
                        root: {
                            fontWeight: 700,
                        },
                    },
                },
                MuiList: {
                    variants: [
                        {
                            props: { variant: 'horizontal' },
                            style: ({ theme }) => ({
                                // display: 'flex',
                                // flexDirection: 'row',
                                // overflowX: 'scroll',
                                // width: '350px',
                                padding: '8px',
                                // borderRadius: theme.shape.borderRadius,
                                // border: 1,
                                '& .MuiListItemIcon-root': {
                                    minWidth: '20px',
                                    color: 'inherit',
                                },
                                '& .MuiListItem-root': {
                                    padding: 0,
                                    minWidth: 50,
                                    '&.active': {
                                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                                    },
                                },
                                '& .MuiListItemText-root': {
                                    textAlign: 'center',
                                },
                            }),
                        },
                        // {
                        //     props: { variant: 'table' },
                        //     style: ({ theme }) => ({
                        //         '& .MuiListButtom-root': {
                        //             border: `1px solid ${theme.palette.divider}`,
                        //             borderRadius:8,
                        //         },
                        //         '& .MuiListItemText-root': {
                        //             fontWeight: 'bold',
                        //         },
                        //     }),
                        // },
                    ],
                },
                MuiListItem: {
                    variants: [
                        {
                            props: { variant: 'outlined' }, // Define a variant personalizada
                            style: ({ theme }) => ({
                                border: '1px solid',
                                borderColor: theme.palette.divider,
                                borderRadius: 8,
                                marginBottom: 4,
                            }),
                        },
                    ],
                },
            },
            typography: {
                fontFamily: 'Quicksand Variable',
                fontSize: 12,
                fontWeightLight: 500,
                fontWeightRegular: 500,
                fontWeightMedium: 600,
                fontWeightBold: 700,
                body1: {
                    fontWeight: 500,
                },
                body2: {
                    fontWeight: 500,
                },
                caption: {
                    fontSize: 12,
                    fontWeight: 500,
                },
                h1: {
                    fontSize: 16,
                    fontWeight: '700',
                    color: 'pallete.primary.main',
                },
                h2: {
                    fontSize: '1.8rem',
                    fontWeight: '800',
                },
                h3: {
                    fontSize: '1.4rem',
                    fontWeight: '700',
                },
                h4: {
                    fontSize: '1.2rem',
                    fontWeight: '800',
                },
                h6: {
                    fontSize: '1.0rem',
                    fontWeight: '500',
                },

                bolded: {
                    fontWeight: '600',
                    color: 'primary.main',
                },
                dashboardBolded: {
                    fontWeight: '600',
                    color: 'primary.main',
                    fontSize: '1.5rem',
                },
                report: {
                    fontWeight: '500',
                    fontSize: '2.3rem',
                    lineHeight: 1,
                },
                'title-sm': {
                    fontWeight: '700',
                    fontSize: '0.8rem',
                    display: 'block',
                },
                'subtitle-sm': {
                    fontWeight: 500,
                    fontSize: '1rem',
                    display: 'block',
                },
                'title-md': {
                    fontWeight: '800',
                    fontSize: '1.375rem', // 22px / 16px = 1.375rem, agora depende do root font-size
                    display: 'block',
                },
                'subtitle-md': {
                    fontWeight: 500,
                    fontSize: '1rem',
                    display: 'block',
                },
                'title-lg': {
                    fontWeight: '700',
                    fontSize: '2.1rem',
                    display: 'block',
                },
                'subtitle-lg': {
                    fontWeight: 500,
                    fontSize: '2rem',
                    display: 'block',
                },
            },
        },
        ptBR,
    );

    const handleReset = () => {
        // navigate(-1, { replace: true });
    };

    return (
        <ErrorBoundary FallbackComponent={ErrorBoundaryComponent} onReset={handleReset}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <CookiesProvider>
                    <AppProvider>
                        <AuthProvider>
                            <ConfirmProvider
                                defaultOptions={{
                                    contentProps: { dividers: true },
                                    allowClose: false,
                                    titleProps: { variant: 'h4' },
                                    cancellationText: 'Cancelar',
                                    confirmationButtonProps: { variant: 'outlined' },
                                }}
                            >
                                <PwaUpdatePrompt />
                                <SnackbarProvider autoHideDuration={3000} variant="success" anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <AppRoutes />
                                    </LocalizationProvider>
                                </SnackbarProvider>
                            </ConfirmProvider>
                        </AuthProvider>
                    </AppProvider>
                </CookiesProvider>
            </ThemeProvider>
        </ErrorBoundary>
    );
}

export default App;
